import { Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Typography, typographyVariants } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { ROUTES, buildRoute } from '@/config/routes';
import { useNavigationService } from '@/services/navigate-service';
import type { PmsSourceKey } from '@/types/pms.types';
import { ConnectionCard, DestructiveNote, SyncNotice } from '../components/ConnectionCard';
import {
  accountHost,
  type ConnectedStatus,
  deriveHeroActions,
  deriveHeroFacts,
  deriveHeroMessage,
  deriveHeroPill,
} from '../components/heroModel';
import { useConnectionHealth } from '../hooks/useConnectionHealth';
import type { PmsAccountRef } from '../services/pmsAccount';
import { useSyncProgress } from '../hooks/useSyncProgress';
import { useIntegrationStatusActions } from '../vendor-settings/useIntegrationStatusActions';
import { getVendorDefinition } from '../vendor-settings/vendorRegistry';
import { landingCopy as copy } from './copy';

interface Props {
  status: ConnectedStatus;
  /** useIntegrationStatus().canManage — role may pause/resume AND there is something to resume. */
  canManage: boolean;
  /** DV-3345: the session's account (the landing is /home only). */
  account: PmsAccountRef | null;
}

/**
 * The connected vendor's card at the top of the Integrations landing (spec
 * §4.4): the shared `ConnectionCard`, wired for the customer. Reads only
 * credential-free state: the status slice's connection summary, the shared
 * sync log, and the server-side stored-credential probe. It never fetches or
 * re-tests credentials (spec §2 decision 4). Every action lives here, not in
 * the card.
 */
export const ConnectionHeroCard = ({ status, canManage, account }: Props) => {
  // Pure lookup, not a hook — safe before the hooks below. Resolved here (not
  // a raw registry index) because `pmsSource` is server data.
  const definition = getVendorDefinition(status.pmsSource);
  const { navigateTo } = useNavigationService();
  // The Disabled pill wins the precedence unconditionally, so probing the
  // stored credentials against the vendor while paused is a pointless live call.
  const health = useConnectionHealth(account, { skip: status.kind === 'disabled' });
  const { sync, startSync, completedOrigin } = useSyncProgress({ origin: 'landing', account });
  const actions = useIntegrationStatusActions({
    vendorName: definition?.displayName ?? status.pmsSource,
    pmsSource: status.pmsSource,
    origin: 'landing',
    account,
  });

  // A fast rejection (no baseline to measure against, or the request failed
  // before any log row) leaves the sync log untouched, so nothing else on the
  // card would say the click did nothing. Cleared by the next attempt.
  const [refreshRequestFailed, setRefreshRequestFailed] = useState(false);
  const refresh = async (pmsSource: PmsSourceKey) => {
    setRefreshRequestFailed(false);
    const outcome = await startSync(pmsSource);
    if (outcome === 'rejected') setRefreshRequestFailed(true);
  };

  if (!definition) return null;
  const vendor = definition;

  const pill = deriveHeroPill(status, health, sync);
  const show = deriveHeroActions(status, sync, canManage);
  const isDisabled = status.kind === 'disabled';
  const goToSettings = () => navigateTo(buildRoute.integrationVendor(vendor.key));

  return (
    <ConnectionCard
      vendor={vendor}
      pill={pill}
      message={deriveHeroMessage(status, pill, vendor)}
      facts={deriveHeroFacts(status, sync)}
      host={accountHost(status.connection.accountUrl)}
      settings={status.connection.settings}
      showSettings={!isDisabled}
      actions={
        <>
          {show.refresh && (
            <Button type="button" variant="ghost" onClick={() => void refresh(vendor.key)}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {copy.hero.actions.refresh}
            </Button>
          )}
          {show.settings && (
            <Button type="button" variant="outline" onClick={goToSettings}>{copy.hero.actions.settings}</Button>
          )}
          {show.viewSettings && (
            <Button type="button" variant="outline" onClick={goToSettings}>{copy.hero.actions.viewSettings}</Button>
          )}
          {show.reenable && (
            <Button type="button" onClick={() => void actions.reenable()} disabled={actions.isReenabling}>
              {actions.isReenabling && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {actions.isReenabling ? copy.hero.actions.reenabling : copy.hero.actions.reenable}
            </Button>
          )}
        </>
      }
      notices={
        <>
          {actions.reenableError && (
            <DestructiveNote role="alert">
              <span className="font-semibold">{copy.hero.reenableFailedTitle}</span> {actions.reenableError}
            </DestructiveNote>
          )}
          {refreshRequestFailed && (
            <DestructiveNote role="alert">
              <span className="font-semibold">{copy.hero.refreshRequestFailed.title}</span>{' '}
              {copy.hero.refreshRequestFailed.body}
            </DestructiveNote>
          )}
        </>
      }
      syncNotice={
        <>
          {!isDisabled && sync.kind === 'running' && (
            <SyncNotice kind="running">
              <Typography variant="body-sm">{copy.hero.sync.running}</Typography>
            </SyncNotice>
          )}
          {!isDisabled && sync.kind === 'failed' && (
            <SyncNotice kind="failed">
              <Typography variant="body-sm">{copy.hero.sync.failed}</Typography>
            </SyncNotice>
          )}
          {!isDisabled && sync.kind === 'complete' && completedOrigin === 'landing' && (
            <SyncNotice kind="complete">
              <Typography variant="body-sm">{copy.hero.sync.complete}</Typography>
              <Link
                to={ROUTES.PROPERTIES}
                className={cn(typographyVariants({ variant: 'body-sm-medium' }), 'text-primary underline-offset-4 hover:underline')}
              >
                {copy.hero.sync.viewProperties}
              </Link>
            </SyncNotice>
          )}
        </>
      }
    />
  );
};
